import React, { useState, useEffect } from 'react';
import { Table, Tag, Input, DatePicker, Select, Space, Card, Typography, Tooltip, Modal, Button, Empty } from 'antd';
import { SearchOutlined, FilterOutlined, HistoryOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../../api/client';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ActivityLogsTab = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailLog, setDetailLog] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ actions: [], modules: [] });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [filters, setFilters] = useState({
        search: '',
        action: null,
        module: null,
        startDate: null,
        endDate: null
    });

    const fetchLogs = async (params = {}) => {
        setLoading(true);
        try {
            const response = await api.get('/admin/activity-logs', {
                params: {
                    page: params.current || pagination.current,
                    per_page: params.pageSize || pagination.pageSize,
                    search: filters.search,
                    action: filters.action,
                    module: filters.module,
                    start_date: filters.startDate,
                    end_date: filters.endDate
                }
            });
            setLogs(response.data.data);
            setPagination({
                ...pagination,
                ...params,
                total: response.data.total
            });
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [filters]);

    useEffect(() => {
        api.get('/admin/activity-logs/filter-options')
            .then(res => setFilterOptions({
                actions: res.data.actions || [],
                modules: res.data.modules || [],
            }))
            .catch(err => console.error('Error fetching filter options:', err));
    }, []);

    const handleTableChange = (newPagination) => {
        fetchLogs(newPagination);
    };

    const getActionColor = (action) => {
        if (!action) return 'default';
        // Destructive / financially sensitive first -- these must never be
        // mistaken for a routine update.
        if (action.includes('DELETE') || action.includes('VOID') || action.includes('DEACTIVATE')) return 'red';
        if (action.includes('CONFIRM') || action.includes('APPROVE')) return 'geekblue';
        if (action.includes('PAYMENT')) return 'volcano';
        if (action.includes('CREATE')) return 'green';
        if (action.includes('UPDATE')) return 'blue';
        if (action.includes('PASSWORD')) return 'gold';
        if (action.includes('PERMISSION')) return 'purple';
        return 'default';
    };

    // Renders the before/after payload the observer stores.
    const renderChanges = (changes) => {
        if (!changes) return <Empty description="No data recorded" />;

        const before = changes.before || null;
        const after = changes.after || null;

        // Business-event logs store flat payloads rather than before/after.
        if (!before && !after) {
            return (
                <Table
                    size="small"
                    pagination={false}
                    rowKey="field"
                    dataSource={Object.entries(changes).map(([field, value]) => ({ field, value }))}
                    columns={[
                        { title: 'Field', dataIndex: 'field', key: 'field', width: 200 },
                        {
                            title: 'Value', dataIndex: 'value', key: 'value',
                            render: (v) => <Text code>{v === null || v === '' ? '—' : String(v)}</Text>
                        },
                    ]}
                />
            );
        }

        const fields = Array.from(new Set([
            ...Object.keys(before || {}),
            ...Object.keys(after || {}),
        ]));

        return (
            <Table
                size="small"
                pagination={false}
                rowKey="field"
                dataSource={fields.map(field => ({
                    field,
                    before: before ? before[field] : undefined,
                    after: after ? after[field] : undefined,
                }))}
                columns={[
                    { title: 'Field', dataIndex: 'field', key: 'field', width: 180 },
                    {
                        title: 'Before', dataIndex: 'before', key: 'before',
                        render: (v) => v === undefined || v === null || v === ''
                            ? <Text type="secondary">—</Text>
                            : <Text delete type="danger">{String(v)}</Text>
                    },
                    {
                        title: 'After', dataIndex: 'after', key: 'after',
                        render: (v) => v === undefined || v === null || v === ''
                            ? <Text type="secondary">—</Text>
                            : <Text type="success" strong>{String(v)}</Text>
                    },
                ]}
            />
        );
    };

    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
            width: 180,
        },
        {
            title: 'User',
            dataIndex: ['user', 'name'],
            key: 'user',
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{text || 'System'}</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{record.user?.email}</Text>
                </Space>
            ),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (action) => (
                <Tag color={getActionColor(action)}>{action}</Tag>
            ),
        },
        {
            title: 'Record',
            key: 'record',
            width: 160,
            render: (_, record) => record.model_type
                ? (
                    <Text style={{ fontSize: 12 }}>
                        {record.model_type}
                        {record.model_id ? <Text type="secondary"> #{record.model_id}</Text> : null}
                    </Text>
                )
                : <Text type="secondary">—</Text>
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => <Tooltip title={text}>{text}</Tooltip>
        },
        {
            title: 'Details',
            key: 'details',
            width: 170,
            render: (_, record) => (
                <Space>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        IP: {record.ip_address || '—'}
                    </Text>
                    {record.changes && (
                        <Button
                            size="small"
                            icon={<HistoryOutlined />}
                            onClick={() => setDetailLog(record)}
                        >
                            Data
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Card title={
            <Space>
                <HistoryOutlined />
                <span>System Activity Logs</span>
            </Space>
        }>
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Space wrap>
                    <Input
                        placeholder="Search logs..."
                        prefix={<SearchOutlined />}
                        allowClear
                        onPressEnter={(e) => setFilters({ ...filters, search: e.target.value })}
                        style={{ width: 250 }}
                    />
                    <Select
                        placeholder="Filter by Module"
                        allowClear
                        style={{ width: 180 }}
                        value={filters.module}
                        onChange={(val) => setFilters({ ...filters, module: val || null })}
                        options={filterOptions.modules.map(m => ({ label: m, value: m }))}
                    />
                    <Select
                        placeholder="Filter by Action"
                        allowClear
                        showSearch
                        style={{ width: 220 }}
                        value={filters.action}
                        onChange={(val) => setFilters({ ...filters, action: val || null })}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={filterOptions.actions.map(a => ({
                            label: a.replace(/_/g, ' '),
                            value: a,
                        }))}
                    />
                    <RangePicker
                        onChange={(dates) => {
                            setFilters({
                                ...filters,
                                startDate: dates ? dates[0].format('YYYY-MM-DD') : null,
                                endDate: dates ? dates[1].format('YYYY-MM-DD') : null
                            });
                        }}
                    />
                </Space>
                <Text type="secondary">Total: {pagination.total} entries</Text>
            </Space>

            <Table
                columns={columns}
                dataSource={logs}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
                size="middle"
                scroll={{ x: 1100 }}
            />

            <Modal
                open={!!detailLog}
                onCancel={() => setDetailLog(null)}
                footer={<Button onClick={() => setDetailLog(null)}>Close</Button>}
                width={760}
                title={detailLog ? (
                    <Space direction="vertical" size={0}>
                        <Space>
                            <Tag color={getActionColor(detailLog.action)}>{detailLog.action}</Tag>
                            <Text>{detailLog.model_type}{detailLog.model_id ? ` #${detailLog.model_id}` : ''}</Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                            {detailLog.user?.name || 'System'} · {dayjs(detailLog.created_at).format('YYYY-MM-DD HH:mm:ss')} · IP {detailLog.ip_address || '—'}
                        </Text>
                    </Space>
                ) : null}
            >
                {detailLog && (
                    <>
                        <Text style={{ display: 'block', marginBottom: 12 }}>{detailLog.description}</Text>
                        {renderChanges(detailLog.changes)}
                    </>
                )}
            </Modal>
        </Card>
    );
};

export default ActivityLogsTab;


